"use client";

import { useReducer, useEffect, useRef, useCallback, useState, useSyncExternalStore, type CSSProperties } from "react";
import { flushSync } from "react-dom";
import { z } from "zod";
import { useWebMcpTool } from "@/lib/webmcp/react";
import { usePlannerWebMcp } from "./usePlannerWebMcp";
import type { FunnelWizardState, AppLocale, FunnelTemplateId, Product, ProjectionInputs, FunnelGraph } from "@/lib/funnel-planner/schema";
import { graphToStages } from "@/lib/funnel-planner/graph-utils";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { PlannerPricingSnapshot } from "@/lib/funnel-planner/package-adviser";
import { ALL_ADVISORY_FLAGS_OFF, type FunnelAdvisoryFlags } from "@/lib/funnel-planner/advisory-flags";
import type { FitBand, FitAudience } from "@/lib/fit-band/engine";
import { createInitialState, TEMPLATE_TO_LEAD_MAGNETS } from "@/lib/funnel-planner/engine";
import {
  saveDraft, loadDraft, clearDraft, watchDraftConsent, noteDraftAction, notePlanningChoice, beginDraftPlan,
} from "@/lib/funnel-planner/storage";
import {
  buildPlannerShareUrl,
  createPlannerShortShareUrl,
  encodePlannerShareState,
  getNodeFromPlannerUrl,
  getStepFromPlannerUrl,
  parseSharedPlannerState,
  replacePlannerStepUrl,
} from "@/lib/funnel-planner/share-url";
import { track } from "@/lib/posthog/events";
import { getPrimaryBookingCta } from "@/lib/primary-cta";
import { localizedPath } from "@/lib/seo/url";
import { getLeadMagnet } from "@/lib/content/entries/lead-magnets";
import { fetchFunnelPdf, triggerPdfDownload } from "@/lib/funnel-planner/submit";
import { computeFunnelVolumeMetrics, scenarioProjection } from "@/lib/funnel-planner/engine";
import { FunnelFlowDiagram } from "@/components/funnel-planner/ui/FunnelFlowDiagram";
import { StepHeader } from "@/components/funnel-planner/ui/StepHeader";
import { StepNav } from "@/components/funnel-planner/ui/StepNav";
import { TemplatePickerStep } from "@/components/funnel-planner/steps/TemplatePickerStep";
import { TrafficChannelsStep } from "@/components/funnel-planner/steps/TrafficChannelsStep";
import { ProductLibraryStep } from "@/components/funnel-planner/steps/ProductLibraryStep";
import { AudienceGoalStep } from "@/components/funnel-planner/steps/AudienceGoalStep";
import { FunnelCanvasStep } from "@/components/funnel-planner/canvas/FunnelCanvasStep";
import { ProjectionStep } from "@/components/funnel-planner/steps/ProjectionStep";
import { ContactCaptureStep } from "@/components/funnel-planner/steps/ContactCaptureStep";
import { FunnelPlannerErrorBoundary } from "@/components/funnel-planner/FunnelPlannerErrorBoundary";
import { FunnelPlannerGamePreview, type InitialGamePreview } from "@/components/funnel-planner/FunnelPlannerGamePreview";
import {
  directionForFunnelLocale,
  formatFunnelCurrency,
  formatFunnelNumber,
  funnelText,
  textAlignForFunnelLocale,
} from "@/lib/funnel-planner/i18n";
import {
  joinAuditTicketGroups,
  STEP_ONE_FIELD_INPUT_TICKETS,
  STEP_ONE_NEXT200_CTA_TICKETS,
  STEP_ONE_NEXT200_GRID_TICKETS,
  STEP_ONE_NEXT200_NAV_TICKETS,
  STEP_ONE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_ONE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_ONE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_ONE_STEP_NAV_SUPPORT_TICKETS,
  STEP_FOUR_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_FOUR_NAV_TICKETS,
  STEP_FOUR_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_FOUR_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_FOUR_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_FOUR_STEP_NAVIGATION_TICKETS,
  STEP_FIVE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_FIVE_NAV_TICKETS,
  STEP_FIVE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_FIVE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_FIVE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_FIVE_STEP_NAVIGATION_TICKETS,
  STEP_SIX_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_SIX_NAV_TICKETS,
  STEP_SIX_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_SIX_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_SIX_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_SIX_STEP_NAVIGATION_TICKETS,
  STEP_SEVEN_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_SEVEN_NAV_TICKETS,
  STEP_SEVEN_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_SEVEN_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_SEVEN_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_SEVEN_STEP_NAVIGATION_TICKETS,
  STEP_TWO_BATCH2_NAV_TICKETS,
  STEP_TWO_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_TWO_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_TWO_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_TWO_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_TWO_STEP_NAVIGATION_TICKETS,
  STEP_THREE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
  STEP_THREE_NAV_TICKETS,
  STEP_THREE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  STEP_THREE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  STEP_THREE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  STEP_THREE_STEP_NAVIGATION_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

type FitQuizHandoff = {
  band?: FitBand;
  audience?: FitAudience;
  from?: string;
};

export type InitialSharedPlannerPlan = {
  state: FunnelWizardState;
  nodeId: string | null;
  slug?: string;
  encodedPlan?: string;
};

type Action = (
  | { type: "SET_TEMPLATE"; templateId: FunnelTemplateId }
  | { type: "SET_AUDIENCE"; audience: FunnelWizardState["audience"] }
  | { type: "SET_GOAL"; goal: string }
  | { type: "SET_REGIONAL_SETTINGS"; settings: FunnelWizardState["regionalSettings"] }
  | { type: "SET_AUDIENCE_GOAL_DETAILS"; details: FunnelWizardState["audienceGoalDetails"] }
  | { type: "SET_CAPACITY_CONFIG"; capacityConfig: FunnelWizardState["capacityConfig"] }
  | { type: "SET_SCENARIOS"; scenarios: FunnelWizardState["scenarios"] }
  | { type: "SET_EXPERIMENTS"; experiments: FunnelWizardState["experiments"] }
  | { type: "SET_TRAFFIC_CHANNELS"; channels: FunnelWizardState["trafficChannels"] }
  | { type: "SET_PRODUCTS"; products: Product[] }
  // v7 session-only actions. These never reach a persisted payload; see
  // lib/funnel-planner/session-fields.ts.
  | { type: "SET_PACKAGE_RECOMMENDATION"; packageRecommendation: FunnelWizardState["packageRecommendation"] }
  | { type: "SET_SEASONAL_DEMAND"; seasonalDemand: FunnelWizardState["seasonalDemand"] }
  | { type: "ADD_PRODUCT"; product: Product }
  | { type: "UPDATE_PRODUCT"; id: string; product: Product }
  | { type: "REMOVE_PRODUCT"; id: string }
  | { type: "SET_STAGES"; stages: FunnelWizardState["stages"] }
  | { type: "SET_GRAPH"; graph: FunnelGraph }
  | { type: "SET_EXPENSES"; expenses: FunnelWizardState["expenses"] }
  | { type: "SET_PROJECTION_INPUT"; key: keyof ProjectionInputs; value: number }
  | { type: "SET_PROJECTION_INPUTS"; inputs: FunnelWizardState["projectionInputs"] }
  | { type: "SET_CONTACT"; contact: FunnelWizardState["contact"] }
  | { type: "GO_TO_STEP"; step: FunnelWizardState["step"] }
  | { type: "COMPLETE" }
  | { type: "RESTORE"; state: FunnelWizardState }
  | { type: "EDIT_FUNNEL" }
) & { source?: "automatic" };

export type PlannerAction = Action;

function syncGraphFromStages(graph: FunnelGraph | undefined, stages: FunnelWizardState["stages"]): FunnelGraph | undefined {
  if (!graph) return graph;
  const stageById = new Map(stages.map((stage) => [stage.id, stage]));
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.type !== "stage") return node;
      const stageId = node.id.startsWith("stage-") ? node.id.slice(6) : node.id;
      const stage = stageById.get(stageId);
      if (!stage) return node;
      return {
        ...node,
        data: {
          ...node.data,
          label: stage.name,
          stageType: stage.stageType,
          conversionRate: stage.conversionRate,
          url: stage.url,
          publishedStatus: stage.publishedStatus,
          owner: stage.owner,
          assetStatus: stage.assetStatus,
          assetUrl: stage.assetUrl,
          assetType: stage.assetType,
          assetSourceSystem: stage.assetSourceSystem,
          assetThumbnailUrl: stage.assetThumbnailUrl,
          assetExecutionStatus: stage.assetExecutionStatus,
          assetCtaNotes: stage.assetCtaNotes,
          trackingStatus: stage.trackingStatus,
          waitDays: stage.waitDays,
          dueInDays: stage.dueInDays,
          actualCvr: stage.actualCvr,
          actualVolume: stage.actualVolume,
          actualPeriodStart: stage.actualPeriodStart,
          actualPeriodEnd: stage.actualPeriodEnd,
          actualPeriodDays: stage.actualPeriodDays,
          requiredStage: stage.requiredStage,
          color: stage.color,
          boxWidth: stage.boxWidth,
          boxHeight: stage.boxHeight,
        },
      };
    }),
  };
}

function reducer(state: FunnelWizardState, action: Action): FunnelWizardState {
  switch (action.type) {
    case "SET_TEMPLATE":
      return { ...state, templateId: action.templateId };
    case "SET_AUDIENCE":
      return { ...state, audience: action.audience };
    case "SET_GOAL":
      return { ...state, goal: action.goal };
    case "SET_REGIONAL_SETTINGS":
      return { ...state, regionalSettings: action.settings };
    case "SET_AUDIENCE_GOAL_DETAILS":
      return { ...state, audienceGoalDetails: action.details };
    case "SET_CAPACITY_CONFIG":
      return { ...state, capacityConfig: action.capacityConfig };
    case "SET_SCENARIOS":
      return { ...state, scenarios: action.scenarios };
    case "SET_EXPERIMENTS":
      return { ...state, experiments: action.experiments };
    case "SET_TRAFFIC_CHANNELS":
      return { ...state, trafficChannels: action.channels };
    case "SET_PRODUCTS":
      return { ...state, products: action.products };
    case "SET_PACKAGE_RECOMMENDATION":
      return { ...state, packageRecommendation: action.packageRecommendation };
    case "SET_SEASONAL_DEMAND":
      return { ...state, seasonalDemand: action.seasonalDemand };
    case "ADD_PRODUCT":
      return {
        ...state,
        products: state.products.length < 5 ? [...state.products, action.product] : state.products,
      };
    case "UPDATE_PRODUCT":
      return {
        ...state,
        products: state.products.map((p) => (p.id === action.id ? action.product : p)),
      };
    case "REMOVE_PRODUCT":
      return {
        ...state,
        products: state.products.filter((p) => p.id !== action.id),
      };
    case "SET_STAGES":
      return { ...state, stages: action.stages, graph: syncGraphFromStages(state.graph, action.stages) };
    case "SET_GRAPH": {
      const stages = graphToStages(action.graph.nodes, action.graph.edges);
      return { ...state, graph: action.graph, stages };
    }
    case "SET_EXPENSES":
      return { ...state, expenses: action.expenses };
    case "SET_PROJECTION_INPUT":
      return {
        ...state,
        projectionInputs: { ...state.projectionInputs, [action.key]: action.value },
      };
    case "SET_PROJECTION_INPUTS":
      return { ...state, projectionInputs: action.inputs };
    case "SET_CONTACT":
      return { ...state, contact: action.contact };
    case "GO_TO_STEP":
      return { ...state, step: action.step };
    case "COMPLETE":
      return { ...state, completedAt: new Date().toISOString(), step: 7 };
    case "RESTORE":
      return action.state;
    case "EDIT_FUNNEL":
      return { ...state, completedAt: null, step: 5 };
    default:
      return state;
  }
}

type SuccessState = {
  templateId: FunnelTemplateId;
  email: string;
};

const TOTAL_STEPS = 7;
const TIER_ONE_SURFACE_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";
const STEP_ONE_EARNED_RESULT_CTA_TICKET =
  joinAuditTicketGroups("P3-024002-cta-step-01-button-planner-action-025-earned-result-cta-reveals-a-pe", STEP_ONE_NEXT200_CTA_TICKETS);
const STEP_ONE_CHUNKED_PROGRESS_TICKET =
  joinAuditTicketGroups(
    "P3-085002-form-step-01-field-planner-input-086-chunked-multi-step-form-progre",
    STEP_ONE_FIELD_INPUT_TICKETS,
    STEP_ONE_NEXT200_NAV_TICKETS,
    STEP_ONE_STEP_NAV_SUPPORT_TICKETS,
  );
const STEP_TWO_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_TWO_BATCH2_NAV_TICKETS,
  STEP_TWO_STEP_NAVIGATION_TICKETS,
);
const STEP_THREE_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_THREE_NAV_TICKETS,
  STEP_THREE_STEP_NAVIGATION_TICKETS,
);
const STEP_FOUR_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_FOUR_NAV_TICKETS,
  STEP_FOUR_STEP_NAVIGATION_TICKETS,
);
const STEP_FIVE_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_FIVE_NAV_TICKETS,
  STEP_FIVE_STEP_NAVIGATION_TICKETS,
);
const STEP_SIX_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_SIX_NAV_TICKETS,
  STEP_SIX_STEP_NAVIGATION_TICKETS,
);
const STEP_SEVEN_CHUNKED_PROGRESS_TICKET = joinAuditTicketGroups(
  STEP_SEVEN_NAV_TICKETS,
  STEP_SEVEN_STEP_NAVIGATION_TICKETS,
);
const STEP_ONE_OFFER_ASSEMBLY_SURFACE_TICKETS = {
  control: joinAuditTicketGroups(
    "P3-100002-interactive-step-01-planner-control-panel-offer-assembly-configurator",
    STEP_ONE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
  ),
  grid: joinAuditTicketGroups(
    "P3-098002-interactive-step-01-planner-game-grid-offer-assembly-configurator",
    STEP_ONE_NEXT200_GRID_TICKETS,
    STEP_ONE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
  ),
  header: joinAuditTicketGroups(
    "P3-099002-interactive-step-01-planner-step-header-offer-assembly-configurator",
    STEP_ONE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
  ),
} as const;
const READINESS_SURFACE_TICKETS_BY_STEP: Partial<Record<FunnelWizardState["step"], {
  readonly control?: string;
  readonly grid?: string;
  readonly header?: string;
}>> = {
  1: {
    control: "P1-100001-interactive-step-01-planner-control-panel-readiness-board",
    grid: "P1-098001-interactive-step-01-planner-game-grid-readiness-board",
    header: "P1-099001-interactive-step-01-planner-step-header-readiness-board",
  },
  2: {
    control: joinAuditTicketGroups(
      "P1-206001-interactive-step-02-planner-control-panel-readiness-board",
      STEP_TWO_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-107001-interactive-step-02-layout-planner-game-grid-001-readiness-board",
        "P1-204001-interactive-step-02-planner-game-grid-readiness-board",
      ].join(";"),
      STEP_TWO_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_TWO_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      "P1-205001-interactive-step-02-planner-step-header-readiness-board",
      STEP_TWO_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
  3: {
    control: joinAuditTicketGroups(
      "P1-306001-interactive-step-03-planner-control-panel-readiness-board",
      STEP_THREE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-214001-interactive-step-03-layout-planner-game-grid-001-readiness-board",
        "P1-304001-interactive-step-03-planner-game-grid-readiness-board",
      ].join(";"),
      STEP_THREE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_THREE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      "P1-305001-interactive-step-03-planner-step-header-readiness-board",
      STEP_THREE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
  4: {
    control: joinAuditTicketGroups(
      "P1-412001-interactive-step-04-planner-control-panel-readiness-board",
      STEP_FOUR_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-314001-interactive-step-04-layout-planner-game-grid-001-readiness-board",
        "P1-410001-interactive-step-04-planner-game-grid-readiness-board",
      ].join(";"),
      STEP_FOUR_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_FOUR_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      "P1-411001-interactive-step-04-planner-step-header-readiness-board",
      STEP_FOUR_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
  5: {
    control: joinAuditTicketGroups(
      "P1-515001-interactive-step-05-planner-control-panel-readiness-board",
      STEP_FIVE_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-420001-interactive-step-05-layout-planner-game-grid-001-readiness-board",
        "P1-513001-interactive-step-05-planner-game-grid-readiness-board",
      ].join(";"),
      STEP_FIVE_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_FIVE_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      "P1-514001-interactive-step-05-planner-step-header-readiness-board",
      STEP_FIVE_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
  6: {
    control: joinAuditTicketGroups(
      "P1-648001-interactive-step-06-planner-control-panel-readiness-board",
      STEP_SIX_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-523001-interactive-step-06-layout-planner-game-grid-001-readiness-board",
        "P1-646001-interactive-step-06-planner-game-grid-readiness-board",
      ].join(";"),
      STEP_SIX_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_SIX_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      "P1-647001-interactive-step-06-planner-step-header-readiness-board",
      STEP_SIX_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
  7: {
    control: joinAuditTicketGroups(
      [
        "P1-749001-interactive-step-07-planner-control-panel-readiness-board",
        "P3-749002-interactive-step-07-planner-control-panel-offer-assembly-configurator",
        "P3-749003-interactive-step-07-planner-control-panel-citation-path-trace",
        "P3-749004-interactive-step-07-planner-control-panel-find-the-gap-page-inspection",
        "P3-749005-interactive-step-07-planner-control-panel-interactive-audit-map",
      ].join(";"),
      STEP_SEVEN_PLANNER_CONTROL_PANEL_SUPPORT_TICKETS,
    ),
    grid: joinAuditTicketGroups(
      [
        "P1-656001-interactive-step-07-layout-planner-game-grid-001-readiness-board",
        "P1-747001-interactive-step-07-planner-game-grid-readiness-board",
        "P3-656002-interactive-step-07-layout-planner-game-grid-001-offer-assembly-configurator",
        "P3-656003-interactive-step-07-layout-planner-game-grid-001-citation-path-trace",
        "P3-656004-interactive-step-07-layout-planner-game-grid-001-find-the-gap-page-inspection",
        "P3-656005-interactive-step-07-layout-planner-game-grid-001-interactive-audit-map",
        "P3-747002-interactive-step-07-planner-game-grid-offer-assembly-configurator",
        "P3-747003-interactive-step-07-planner-game-grid-citation-path-trace",
        "P3-747004-interactive-step-07-planner-game-grid-find-the-gap-page-inspection",
        "P3-747005-interactive-step-07-planner-game-grid-interactive-audit-map",
      ].join(";"),
      STEP_SEVEN_LAYOUT_GAME_GRID_SUPPORT_TICKETS,
      STEP_SEVEN_PLANNER_GAME_GRID_SUPPORT_TICKETS,
    ),
    header: joinAuditTicketGroups(
      [
        "P1-748001-interactive-step-07-planner-step-header-readiness-board",
        "P3-748002-interactive-step-07-planner-step-header-offer-assembly-configurator",
        "P3-748003-interactive-step-07-planner-step-header-citation-path-trace",
        "P3-748004-interactive-step-07-planner-step-header-find-the-gap-page-inspection",
        "P3-748005-interactive-step-07-planner-step-header-interactive-audit-map",
      ].join(";"),
      STEP_SEVEN_PLANNER_STEP_HEADER_SUPPORT_TICKETS,
    ),
  },
};

function joinAuditTickets(...ticketGroups: Array<string | null | undefined>): string | undefined {
  const tickets = ticketGroups
    .flatMap((group) => String(group || "").split(";"))
    .map((ticket) => ticket.trim())
    .filter(Boolean);
  return tickets.length > 0 ? Array.from(new Set(tickets)).join(";") : undefined;
}

let hydrationStoreValue = false;
const hydrationStoreListeners = new Set<() => void>();

const subscribeHydration = (listener: () => void) => {
  hydrationStoreListeners.add(listener);
  if (!hydrationStoreValue && typeof window !== "undefined") {
    window.setTimeout(() => {
      hydrationStoreValue = true;
      hydrationStoreListeners.forEach((candidate) => candidate());
    }, 0);
  }
  return () => {
    hydrationStoreListeners.delete(listener);
  };
};
const getClientHydrationSnapshot = () => hydrationStoreValue;
const getServerHydrationSnapshot = () => false;
const DRAFT_CONFLICT_SHELL_STYLE: CSSProperties = {
  display: "grid",
  minHeight: "min(72vh, 44rem)",
  width: "100%",
  placeItems: "center",
  padding: "clamp(1rem, 4vw, 2rem)",
};
const DRAFT_CONFLICT_CARD_STYLE: CSSProperties = {
  width: "min(100%, 44rem)",
  border: "1px solid var(--marketing-border-strong)",
  borderRadius: "var(--seoh-radius-lg)",
  background:
    "linear-gradient(180deg, var(--marketing-panel-glass-start), var(--marketing-panel-glass-end))",
  boxShadow: "var(--marketing-shadow-strong)",
  padding: "clamp(1.25rem, 4vw, 2rem)",
};
const DRAFT_CONFLICT_ACTIONS_STYLE: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 14rem), 1fr))",
  gap: "0.75rem",
  marginTop: "1.4rem",
};

type ShareStatus = "idle" | "copied" | "failed";

type PlannerReadinessSurfaceEvidence = {
  readonly blockersBucket: string;
  readonly currentState: string;
  readonly liveLabel: string;
  readonly nextLabel: string;
  readonly progressLabel: string;
  readonly readyCount: number;
  readonly tier: string;
  readonly totalCount: number;
};

function buildPlannerReadinessSurfaceEvidence(
  content: FunnelPlannerContent,
  state: FunnelWizardState,
): PlannerReadinessSurfaceEvidence {
  const checks = [
    {
      ready: Boolean(state.templateId),
      label: funnelText(content, "gamificationReadinessTemplateLabel", "Funnel type selected"),
    },
    {
      ready: state.trafficChannels.some((channel) => channel.monthlyVisitors > 0),
      label: funnelText(content, "gamificationReadinessTrafficLabel", "Traffic inputs mapped"),
    },
    {
      ready: state.products.length > 0,
      label: funnelText(content, "gamificationReadinessOfferLabel", "Offer stack defined"),
    },
    {
      ready: Boolean(state.audience && state.goal.trim()),
      label: funnelText(content, "gamificationReadinessAudienceLabel", "Audience and goal captured"),
    },
    {
      ready: ((state.graph?.nodes.filter((node) => node.type === "stage").length ?? state.stages.length) >= 1),
      label: funnelText(content, "gamificationReadinessStagesLabel", "Funnel stages mapped"),
    },
    {
      ready: state.step >= 6 || state.projectionInputs.monthlyVisitors > 0,
      label: funnelText(content, "gamificationReadinessProjectionLabel", "Projection reviewed"),
    },
    {
      ready: Boolean(state.completedAt),
      label: funnelText(content, "gamificationReadinessHandoffLabel", "Handoff ready"),
    },
  ];
  const readyCount = checks.filter((check) => check.ready).length;
  const totalCount = checks.length;
  const missingCount = totalCount - readyCount;
  const tier =
    readyCount >= totalCount ? "handoff_ready" : readyCount >= 5 ? "review_ready" : readyCount >= 3 ? "draft_plus" : "draft";
  const nextLabel = checks.find((check) => !check.ready)?.label
    ?? funnelText(content, "gamificationPreviewReadinessNoBlockers", "No blockers marked.");
  const progressLabel = funnelText(content, "gamificationReadinessProgress", "{ready} of {total} checks ready", {
    ready: readyCount,
    total: totalCount,
  });
  const currentState =
    readyCount >= totalCount
      ? "complete_result"
      : readyCount === 0
        ? "untouched_empty"
        : READINESS_SURFACE_TICKETS_BY_STEP[state.step]
          ? "active_inprogress"
          : "partial";
  const blockersBucket =
    missingCount <= 0 ? "none" : missingCount <= 2 ? "one_two" : missingCount <= 4 ? "three_four" : "five_plus";

  return {
    blockersBucket,
    currentState,
    liveLabel: funnelText(content, "gamificationPreviewReadinessLiveSummary", "{ready} of {total} checks ready. Tier: {tier}. Next: {next}.", {
      ready: readyCount,
      total: totalCount,
      tier,
      next: nextLabel,
    }),
    nextLabel,
    progressLabel,
    readyCount,
    tier,
    totalCount,
  };
}

function plannerReadinessSurfaceAttributes(evidence: PlannerReadinessSurfaceEvidence) {
  return {
    "data-blockers-bucket": evidence.blockersBucket,
    "data-gamification-current-state": evidence.currentState,
    "data-gamification-reduced-motion-static": "supported",
    "data-gamification-supported-states": TIER_ONE_SURFACE_SUPPORTED_STATES,
    "data-readiness-ready-count": evidence.readyCount,
    "data-readiness-tier": evidence.tier,
    "data-readiness-total-count": evidence.totalCount,
    "data-tier-one-family": "readiness_board",
  } as const;
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy selection path.
  }

  try {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

export function FunnelPlannerWizard({
  content,
  locale,
  fitQuizHandoff,
  headingLevel = "h1",
  initialSharedPlan,
  pricingSnapshot,
  advisoryFlags = ALL_ADVISORY_FLAGS_OFF,
  shareBasePath,
  initialGamePreview,
}: {
  content: FunnelPlannerContent;
  locale: AppLocale;
  fitQuizHandoff?: FitQuizHandoff;
  headingLevel?: "h1" | "h2";
  initialSharedPlan?: InitialSharedPlannerPlan;
  /**
   * Caller-owned indicative pricing, resolved on the server. Absent means the
   * NL-041 adviser fails closed to manual scoping rather than guessing a price.
   */
  pricingSnapshot?: PlannerPricingSnapshot | null;
  /** Server-resolved NL-041/NL-010/NL-014 kill switches. Off unless configured. */
  advisoryFlags?: FunnelAdvisoryFlags;
  shareBasePath?: string;
  initialGamePreview?: InitialGamePreview;
}) {
  const [state, reducerDispatch] = useReducer(
    reducer,
    null,
    () =>
      createInitialState({
        fitQuizBand: fitQuizHandoff?.band ?? null,
        fitQuizAudience: fitQuizHandoff?.audience ?? null,
      }),
  );

  const dispatch = useCallback((action: Action) => {
    noteDraftAction(action.type, action.source);
    reducerDispatch(action);
  }, []);
  // Each mount starts another active plan, then keeps withdrawal effective even when the planner state does not change.
  useEffect(() => {
    beginDraftPlan();
    return watchDraftConsent();
  }, []);

  const [draftDiscarded, setDraftDiscarded] = useState(false);
  const [draftNotSaved, setDraftNotSaved] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [savedDraftConflict, setSavedDraftConflict] = useState<FunnelWizardState | null>(null);
  const [successState, setSuccessState] = useState<SuccessState | null>(null);
  const [shareStatus, setShareStatus] = useState<ShareStatus>("idle");
  const [agentSharedReadOnly, setAgentSharedReadOnly] = useState(Boolean(initialSharedPlan));
  const hydrated = useSyncExternalStore(
    subscribeHydration,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  );
  const stepStartedAt = useRef<number | null>(null);
  const abandonSent = useRef(false);
  const draftNotSavedRef = useRef(false);
  const latestStateRef = useRef(state);
  const canvasPendingSyncRef = useRef(false);
  const canvasFlushRef = useRef<(() => void) | null>(null);
  const activeDeepLinkNodeRef = useRef<string | null>(null);
  const hasFitQuizHandoff = fitQuizHandoff?.from === "fit-quiz";
  const hasInitialSharedPlan = Boolean(initialSharedPlan?.state);
  const plannerDirection = directionForFunnelLocale(locale);
  const plannerTextAlign = textAlignForFunnelLocale(locale);
  const plannerDirectionalStyle: CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string> = {
    direction: plannerDirection,
    textAlign: plannerTextAlign,
    "--funnel-planner-text-align": plannerTextAlign,
    "--funnel-planner-direction": plannerDirection,
  };

  useEffect(() => {
    document.body.setAttribute("data-funnel-planner-active", "true");
    return () => {
      document.body.removeAttribute("data-funnel-planner-active");
    };
  }, []);

  useEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  // Restore draft after hydration to avoid SSR/client mismatch.
  useEffect(() => {
    if (initialSharedPlan?.state) {
      dispatch({ type: "RESTORE", state: initialSharedPlan.state });
      activeDeepLinkNodeRef.current = initialSharedPlan.state.step === 5 ? initialSharedPlan.nodeId : null;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- browser-only restore happens after hydration.
      setDraftReady(true);
      track("funnel_planner_share_url_opened", {
        step: initialSharedPlan.state.step,
        template_id: initialSharedPlan.state.templateId ?? undefined,
        locale,
      });
      return;
    }

    const shared = parseSharedPlannerState(window.location.search);
    if (shared.state) {
      setAgentSharedReadOnly(true);
      dispatch({ type: "RESTORE", state: shared.state });
      activeDeepLinkNodeRef.current = shared.state.step === 5 ? shared.nodeId : null;
      replacePlannerStepUrl(shared.state.step, shared.nodeId);
      setDraftReady(true);
      track("funnel_planner_share_url_opened", {
        step: shared.state.step,
        template_id: shared.state.templateId ?? undefined,
        locale,
      });
      return;
    }

    const stepFromUrl = getStepFromPlannerUrl(window.location.search);
    const nodeFromUrl = getNodeFromPlannerUrl(window.location.search);
    activeDeepLinkNodeRef.current = stepFromUrl === 5 ? nodeFromUrl : null;
    const draft = loadDraft();
    if (draft.state) {
      const stateFromDraft = stepFromUrl ? { ...draft.state, step: stepFromUrl } : draft.state;
      if (hasFitQuizHandoff) {
        setSavedDraftConflict(stateFromDraft);
      } else {
        dispatch({ type: "RESTORE", state: stateFromDraft });
      }
    } else if (stepFromUrl) {
      dispatch({ type: "GO_TO_STEP", step: stepFromUrl });
    }
    if (draft.discarded) {
      setDraftDiscarded(true);
    }
    setDraftReady(true);
  }, [dispatch, hasFitQuizHandoff, initialSharedPlan, locale]);

  // Draft auto-save
  useEffect(() => {
    if (!draftReady || savedDraftConflict) return;
    if (state.completedAt) return;
    const result = saveDraft(state);
    const shouldWarn = result === "quota_exceeded";
    if (draftNotSavedRef.current !== shouldWarn) {
      draftNotSavedRef.current = shouldWarn;
      setDraftNotSaved(shouldWarn);
    }
  }, [draftReady, savedDraftConflict, state]);

  useEffect(() => {
    if (!draftReady || savedDraftConflict) return;
    if (hasInitialSharedPlan) return;
    replacePlannerStepUrl(state.step, state.step === 5 ? activeDeepLinkNodeRef.current : null);
  }, [draftReady, hasInitialSharedPlan, savedDraftConflict, state.step]);

  // Track step views
  useEffect(() => {
    stepStartedAt.current = Date.now();
    track("funnel_planner_step_viewed", {
      step: state.step,
      template_id: state.templateId ?? undefined,
      locale,
    });
  }, [state.step, locale, state.templateId]);

  // Fit Quiz analytics linkage
  useEffect(() => {
    if (fitQuizHandoff?.from === "fit-quiz") {
      track("funnel_planner_started", {
        template_id: state.templateId ?? undefined,
        locale,
        from: "fit-quiz",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abandonment tracking
  useEffect(() => {
    const fireAbandon = () => {
      if (state.completedAt || abandonSent.current) return;
      if (state.step < 7) {
        abandonSent.current = true;
        track("funnel_planner_abandoned", {
          step: state.step,
          template_id: state.templateId ?? undefined,
          locale,
        });
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") fireAbandon();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("pagehide", fireAbandon);
    window.addEventListener("beforeunload", fireAbandon);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("pagehide", fireAbandon);
      window.removeEventListener("beforeunload", fireAbandon);
    };
  }, [state.step, state.completedAt, state.templateId, locale]);

  const handleNext = useCallback(() => {
    if (canvasPendingSyncRef.current) {
      canvasFlushRef.current?.();
    }
    const now = Date.now();
    const timeOnStep = stepStartedAt.current != null ? now - stepStartedAt.current : 0;
    track("funnel_planner_step_completed", {
      step: state.step,
      template_id: state.templateId ?? undefined,
      locale,
      time_on_step_ms: timeOnStep,
    });
    dispatch({ type: "GO_TO_STEP", step: (state.step + 1) as FunnelWizardState["step"] });
  }, [state.step, state.templateId, locale]);

  const handleBack = useCallback(() => {
    dispatch({ type: "GO_TO_STEP", step: (state.step - 1) as FunnelWizardState["step"] });
  }, [state.step]);

  const handleSuccess = useCallback((email: string, templateId: FunnelTemplateId) => {
    clearDraft();
    dispatch({ type: "COMPLETE" });
    setDraftNotSaved(false);
    draftNotSavedRef.current = false;
    setSuccessState({ email, templateId });
  }, []);

  const handleEditFunnel = useCallback(() => {
    dispatch({ type: "EDIT_FUNNEL" });
    setSuccessState(null);
  }, []);

  const handleReset = useCallback(() => {
    clearDraft();
    dispatch({ type: "RESTORE", state: createInitialState({ fitQuizBand: null, fitQuizAudience: null }) });
    activeDeepLinkNodeRef.current = null;
    setDraftNotSaved(false);
    draftNotSavedRef.current = false;
    setSuccessState(null);
  }, []);

  const flushPlannerState = useCallback(async () => {
    if (canvasPendingSyncRef.current) {
      flushSync(() => canvasFlushRef.current?.());
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
    }
    return latestStateRef.current;
  }, []);

  const handleSharePlan = useCallback(async (signal?: AbortSignal) => {
    const latestState = await flushPlannerState();
    signal?.throwIfAborted();
    const existingShortShareUrl = initialSharedPlan?.encodedPlan && encodePlannerShareState(latestState) === initialSharedPlan.encodedPlan
      ? (() => {
          const url = new URL(window.location.href);
          url.search = "";
          url.hash = "";
          return url.toString();
        })()
      : null;
    const shortShareUrl = existingShortShareUrl ?? await createPlannerShortShareUrl(latestState, locale, null, shareBasePath, signal);
    signal?.throwIfAborted();
    const shareUrl = shortShareUrl ?? buildPlannerShareUrl(latestState, null, shareBasePath);
    const copied = await copyTextToClipboard(shareUrl);
    setShareStatus(copied ? "copied" : "failed");
    track("funnel_planner_share_url_created", {
      step: latestState.step,
      template_id: latestState.templateId ?? undefined,
      locale,
      copied,
      share_url_type: shortShareUrl ? "short" : "encoded_fallback",
    });
    window.setTimeout(() => setShareStatus("idle"), 2400);
    return { url: shareUrl, copied };
  }, [flushPlannerState, initialSharedPlan, locale, shareBasePath]);

  usePlannerWebMcp({ state, content, ready: hydrated && draftReady && !successState,
    readOnly: agentSharedReadOnly, locale, flush: flushPlannerState, share: handleSharePlan,
    dispatch: (action) => flushSync(() => dispatch(action)),
  });

  const handleResumeSavedDraft = useCallback(() => {
    if (!savedDraftConflict) return;
    dispatch({ type: "RESTORE", state: savedDraftConflict });
    setSavedDraftConflict(null);
    setDraftNotSaved(false);
    draftNotSavedRef.current = false;
    setSuccessState(null);
  }, [savedDraftConflict]);

  const handleStartFromQuizResult = useCallback(() => {
    clearDraft();
    dispatch({
      type: "RESTORE",
      state: createInitialState({
        fitQuizBand: fitQuizHandoff?.band ?? null,
        fitQuizAudience: fitQuizHandoff?.audience ?? null,
      }),
    });
    // Choosing the quiz result is fresh planning input, unlike loading or restoring a URL or draft, so it is saved.
    notePlanningChoice();
    activeDeepLinkNodeRef.current = null;
    setSavedDraftConflict(null);
    setDraftNotSaved(false);
    draftNotSavedRef.current = false;
    setSuccessState(null);
  }, [fitQuizHandoff?.audience, fitQuizHandoff?.band]);

  if (successState) {
    return (
      <SuccessScreen
        content={content}
        locale={locale}
        successState={successState}
        wizardState={state}
        onEditFunnel={handleEditFunnel}
      />
    );
  }

  if (savedDraftConflict) {
    return (
      <FunnelPlannerErrorBoundary message={funnelText(content, "errorBoundaryMessage", "Something went wrong. Please refresh.")}>
        <div
          dir={plannerDirection}
          data-funnel-planner-shell="wizard"
          data-funnel-planner-direction={plannerDirection}
          data-funnel-planner-hydrated={hydrated ? "true" : "false"}
          data-funnel-planner-step={state.step}
          data-funnel-planner-draft-conflict="true"
          style={{ ...DRAFT_CONFLICT_SHELL_STYLE, ...plannerDirectionalStyle }}
        >
          <section
            aria-labelledby="funnel-planner-draft-conflict-title"
            style={DRAFT_CONFLICT_CARD_STYLE}
          >
            <div
              aria-hidden="true"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 5,
                marginBottom: "1.25rem",
              }}
            >
              {Array.from({ length: TOTAL_STEPS }, (_, index) => (
                <span
                  key={index}
                  style={{
                    height: 6,
                    borderRadius: 999,
                    background:
                      index === 0
                        ? "var(--marketing-accent)"
                        : "color-mix(in srgb, var(--marketing-accent) 20%, transparent)",
                  }}
                />
              ))}
            </div>
            <h1
              id="funnel-planner-draft-conflict-title"
              style={{
                margin: 0,
                color: "var(--marketing-text)",
                fontFamily: "var(--font-primary)",
                fontSize: "clamp(1.65rem, 3vw, 2.35rem)",
                lineHeight: 1.08,
              }}
            >
              {funnelText(content, "draftConflictTitle", "Resume your saved funnel plan?")}
            </h1>
            <p
              style={{
                margin: "0.75rem 0 0",
                color: "var(--marketing-muted)",
                fontSize: "1rem",
                lineHeight: 1.65,
              }}
            >
              {funnelText(
                content,
                "draftConflictBody",
                "You came from the Fit Quiz, but there is a saved planner draft on this device. Resume the saved plan or start from the quiz result.",
              )}
            </p>
            <div style={DRAFT_CONFLICT_ACTIONS_STYLE}>
              <button
                type="button"
                onClick={handleResumeSavedDraft}
                data-funnel-planner-resume-draft
                data-seoh-xp-zone="static"
                data-seoh-xp-visible="true"
                className="marketing-button marketing-button--primary"
                style={{ width: "100%", transform: "none", transition: "none" }}
              >
                {funnelText(content, "draftConflictResumeButton", "Resume saved plan")}
              </button>
              <button
                type="button"
                onClick={handleStartFromQuizResult}
                data-funnel-planner-start-from-quiz
                data-seoh-xp-zone="static"
                data-seoh-xp-visible="true"
                className="marketing-button marketing-button--secondary"
                style={{ width: "100%", transform: "none", transition: "none" }}
              >
                {funnelText(content, "draftConflictStartFromQuizButton", "Start from quiz result")}
              </button>
            </div>
          </section>
        </div>
      </FunnelPlannerErrorBoundary>
    );
  }

  const readinessSurfaceTickets = READINESS_SURFACE_TICKETS_BY_STEP[state.step];
  const plannerReadinessSurface = readinessSurfaceTickets
    ? buildPlannerReadinessSurfaceEvidence(content, state)
    : null;
  const plannerReadinessAttributes = plannerReadinessSurface
    ? plannerReadinessSurfaceAttributes(plannerReadinessSurface)
    : {};
  const stepOneOfferSurfaceTickets = state.step === 1 ? STEP_ONE_OFFER_ASSEMBLY_SURFACE_TICKETS : null;
  const gridAuditTicket = joinAuditTickets(readinessSurfaceTickets?.grid, stepOneOfferSurfaceTickets?.grid);
  const controlAuditTicket = joinAuditTickets(readinessSurfaceTickets?.control, stepOneOfferSurfaceTickets?.control);
  const headerAuditTicket = joinAuditTickets(readinessSurfaceTickets?.header, stepOneOfferSurfaceTickets?.header);
  const earnedResultCtaState = state.templateId ? "complete_result" : "active_inprogress";
  const stepNavAuditTicket = state.step === 1
    ? STEP_ONE_CHUNKED_PROGRESS_TICKET
    : state.step === 2
      ? STEP_TWO_CHUNKED_PROGRESS_TICKET
      : state.step === 3
        ? STEP_THREE_CHUNKED_PROGRESS_TICKET
        : state.step === 4
          ? STEP_FOUR_CHUNKED_PROGRESS_TICKET
          : state.step === 5
            ? STEP_FIVE_CHUNKED_PROGRESS_TICKET
            : state.step === 6
              ? STEP_SIX_CHUNKED_PROGRESS_TICKET
              : state.step === 7
                ? STEP_SEVEN_CHUNKED_PROGRESS_TICKET
                : undefined;
  const stepNavFamily = stepNavAuditTicket ? "chunked_form_progress" : undefined;

  return (
    <FunnelPlannerErrorBoundary message={funnelText(content, "errorBoundaryMessage", "Something went wrong. Please refresh.")}>
      <div
        dir={plannerDirection}
        data-funnel-planner-shell="wizard"
        data-funnel-planner-direction={plannerDirection}
        data-funnel-planner-hydrated={hydrated ? "true" : "false"}
        data-funnel-planner-step={state.step}
        data-analytics-component-family="funnel_planner"
        data-analytics-component-id="funnel-planner.wizard"
        data-analytics-experience-id="xp.funnel-planner.wizard.control"
        data-analytics-variant-key={state.templateId ?? "control"}
        className="funnel-planner-shell funnel-planner-shell--wizard mx-auto px-4 py-8 md:py-12"
        style={{
          ...plannerDirectionalStyle,
          width: "100%",
          maxWidth: state.step === 5 ? "1280px" : state.step === 6 ? "1120px" : state.step === 2 ? "960px" : "760px",
          margin: "0 auto",
          padding: "clamp(1.5rem, 3vw, 3rem) clamp(1rem, 3vw, 2rem)",
          boxSizing: "border-box",
        }}
      >
        <div
          data-funnel-planner-game-grid="true"
          data-audit-ticket={gridAuditTicket}
          {...plannerReadinessAttributes}
        >
          {plannerReadinessSurface ? (
            <span className="sr-only" role="status" aria-live="polite">
              {plannerReadinessSurface.liveLabel}
            </span>
          ) : null}
          <div
            data-funnel-planner-control-column="true"
            data-audit-ticket={controlAuditTicket}
            {...plannerReadinessAttributes}
          >
            {plannerReadinessSurface ? (
              <span className="sr-only" role="status" aria-live="polite">
                {plannerReadinessSurface.liveLabel}
              </span>
            ) : null}
            {draftDiscarded && (
              <div className="mb-4 rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800" role="alert">
                {content.draftDiscardedWarning}
              </div>
            )}
            {draftNotSaved && (
              <div className="mb-4 rounded-md bg-amber-50 px-4 py-2 text-sm text-amber-800" role="alert">
                {content.draftNotSavedWarning}
              </div>
            )}

            <div className="flex items-start justify-between gap-4">
              <StepHeader
                auditTicket={headerAuditTicket}
                step={state.step}
                totalSteps={TOTAL_STEPS}
                content={content}
                headingLevel={headingLevel}
                readinessSurface={plannerReadinessSurface ? {
                  blockersBucket: plannerReadinessSurface.blockersBucket,
                  currentState: plannerReadinessSurface.currentState,
                  liveLabel: plannerReadinessSurface.liveLabel,
                  nextLabel: plannerReadinessSurface.nextLabel,
                  progressLabel: plannerReadinessSurface.progressLabel,
                  readyCount: plannerReadinessSurface.readyCount,
                  supportedStates: TIER_ONE_SURFACE_SUPPORTED_STATES,
                  tier: plannerReadinessSurface.tier,
                  totalCount: plannerReadinessSurface.totalCount,
                } : undefined}
              />
              <div className="mt-1 flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => void handleSharePlan()}
                  data-analytics-component-family="cta"
                  data-analytics-component-id="funnel-planner.cta.share"
                  data-analytics-cta-id="cta.funnel-planner.share"
                  data-analytics-experience-id="xp.funnel-planner.wizard.control"
                  data-analytics-variant-key={state.templateId ?? "control"}
                  data-funnel-planner-share
                  data-funnel-planner-earned-result-cta={state.step === 1 ? "true" : undefined}
                  data-audit-ticket={state.step === 1 ? STEP_ONE_EARNED_RESULT_CTA_TICKET : undefined}
                  data-tier-one-family={state.step === 1 ? "earned_result_cta" : undefined}
                  data-gamification-current-state={state.step === 1 ? earnedResultCtaState : undefined}
                  data-gamification-reduced-motion-static={state.step === 1 ? "supported" : undefined}
                  data-gamification-supported-states={state.step === 1 ? TIER_ONE_SURFACE_SUPPORTED_STATES : undefined}
                  data-readiness-ready-count={state.step === 1 ? (state.templateId ? 1 : 0) : undefined}
                  data-readiness-total-count={state.step === 1 ? 1 : undefined}
                  data-readiness-tier={state.step === 1 ? (state.templateId ? "earned_artifact_ready" : "template_needed") : undefined}
                  data-blockers-bucket={state.step === 1 ? (state.templateId ? "none" : "one") : undefined}
                  className="rounded-md border border-blue-500/50 bg-blue-500/10 px-3 py-1.5 text-xs font-semibold text-blue-200 hover:bg-blue-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label={funnelText(content, "sharePlanAriaLabel", "Copy an editable share link for this funnel planner step")}
                >
                  {shareStatus === "copied"
                    ? funnelText(content, "sharePlanCopied", "Link copied")
                    : shareStatus === "failed"
                      ? funnelText(content, "sharePlanFailed", "Copy failed")
                      : funnelText(content, "sharePlanButton", "Share")}
                  {state.step === 1 ? (
                    <span className="sr-only" role="status" aria-live="polite">
                      {shareStatus === "copied"
                        ? funnelText(content, "sharePlanCopied", "Link copied")
                        : funnelText(content, "sharePlanAriaLabel", "Copy an editable share link for this funnel planner step")}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                  aria-label={funnelText(content, "startOverAriaLabel", "Start the funnel planner over from the beginning")}
                >
                  {funnelText(content, "startOverButton", "Start over")}
                </button>
              </div>
            </div>

            <StepNav
              step={state.step}
              totalSteps={TOTAL_STEPS}
              onNext={handleNext}
              onBack={handleBack}
              canGoNext={isStepValid(state)}
              showNext={state.step < 7}
              content={content}
              gamificationAuditTicket={stepNavAuditTicket}
              gamificationFamily={stepNavFamily}
              gamificationCurrentState={stepNavAuditTicket ? "active_inprogress" : undefined}
              gamificationLiveLabel={stepNavAuditTicket
                ? funnelText(content, "stepLiveRegionLabel", "Funnel planner step {step} of {total}", {
                  step: state.step,
                  total: TOTAL_STEPS,
                })
                : undefined}
              readinessReadyCount={stepNavAuditTicket ? state.step : undefined}
              readinessTotalCount={stepNavAuditTicket ? TOTAL_STEPS : undefined}
              readinessTier={stepNavAuditTicket ? `chunked_step_${state.step}` : undefined}
              blockersBucket={stepNavAuditTicket ? "five_plus" : undefined}
              supportedStates={stepNavAuditTicket ? TIER_ONE_SURFACE_SUPPORTED_STATES : undefined}
            />

            <div
              dir={plannerDirection}
              className="mt-6"
              data-funnel-planner-step-body
              data-funnel-planner-direction={plannerDirection}
              style={{
                direction: plannerDirection,
                textAlign: plannerTextAlign,
              }}
              aria-live="polite"
              aria-label={funnelText(content, "stepLiveRegionLabel", "Funnel planner step {step} of {total}", {
                step: state.step,
                total: TOTAL_STEPS,
              })}
            >
              {state.step === 1 && (
                <TemplatePickerStep state={state} dispatch={dispatch} content={content} locale={locale} />
              )}
              {state.step === 2 && (
                <TrafficChannelsStep state={state} dispatch={dispatch} content={content} locale={locale} />
              )}
              {state.step === 3 && (
                <ProductLibraryStep
                  audience={state.audience}
                  packageRecommendation={state.packageRecommendation}
                  pricingSnapshot={pricingSnapshot ?? null}
                  advisoryFlags={advisoryFlags}
                  advisoryReadOnly={hasInitialSharedPlan}
                  products={state.products}
                  stages={state.stages}
                  templateId={state.templateId}
                  onPackageRecommendationChange={(packageRecommendation) =>
                    dispatch({ type: "SET_PACKAGE_RECOMMENDATION", packageRecommendation })
                  }
                  onProductsChange={(products) => dispatch({ type: "SET_PRODUCTS", products })}
                  content={content}
                  locale={locale}
                  regionalSettings={state.regionalSettings}
                />
              )}
              {state.step === 4 && (
                <AudienceGoalStep state={state} dispatch={dispatch} content={content} />
              )}
              {state.step === 5 && (
                <FunnelCanvasStep
                  state={state}
                  dispatch={dispatch}
                  content={content}
                  locale={locale}
                  pendingSyncRef={canvasPendingSyncRef}
                  flushRef={canvasFlushRef}
                />
              )}
              {state.step === 6 && (
                <ProjectionStep
                  state={state}
                  dispatch={dispatch}
                  content={content}
                  locale={locale}
                  pricingSnapshot={pricingSnapshot ?? null}
                  advisoryFlags={advisoryFlags}
                  advisoryReadOnly={hasInitialSharedPlan}
                />
              )}
              {state.step === 7 && (
                <ContactCaptureStep
                  state={state}
                  dispatch={dispatch}
                  content={content}
                  locale={locale}
                  onSuccess={handleSuccess}
                />
              )}
            </div>
          </div>
          <FunnelPlannerGamePreview content={content} locale={locale} state={state} initialPreview={initialGamePreview} />
        </div>
      </div>
    </FunnelPlannerErrorBoundary>
  );
}

function isStepValid(state: FunnelWizardState): boolean {
  switch (state.step) {
    case 1: return state.templateId !== null;
    case 2: return state.trafficChannels.length >= 1 && state.trafficChannels.some((c) => c.monthlyVisitors > 0);
    case 3: return true; // products optional
    case 4: return state.audience !== null && state.goal.trim().length > 0;
    case 5: return (state.graph?.nodes.filter(n => n.type === "stage").length ?? state.stages.length) >= 1;
    case 6: return true; // projection always valid
    default: return true;
  }
}

function SuccessScreen({
  content,
  locale,
  successState,
  wizardState,
  onEditFunnel,
}: {
  content: FunnelPlannerContent;
  locale: AppLocale;
  successState: SuccessState;
  wizardState: FunnelWizardState;
  onEditFunnel: () => void;
}) {
  const bookingCta = getPrimaryBookingCta(locale, {
    campaign: "funnel_planner_success",
    content: successState.templateId,
    medium: "funnel_planner",
  });

  const relatedMagnets = (TEMPLATE_TO_LEAD_MAGNETS[successState.templateId] ?? [])
    .slice(0, 2)
    .map((slug: string) => getLeadMagnet(slug))
    .filter((m): m is NonNullable<ReturnType<typeof getLeadMagnet>> => m != null);

  const [redownloading, setRedownloading] = useState(false);

  const handleRedownload = async (signal?: AbortSignal) => {
    setRedownloading(true);
    try {
      const blob = await fetchFunnelPdf(wizardState, locale, { signal });
      signal?.throwIfAborted();
      triggerPdfDownload(blob, successState.templateId);
    } finally {
      setRedownloading(false);
    }
  };

  useWebMcpTool({ action: "export_funnel_plan", schema: z.strictObject({}),
    preview: () => JSON.stringify({ format: "PDF", templateId: successState.templateId, includesSubmittedContact: true }),
    execute: async (_, { signal }) => {
      if (redownloading) return { status: "unavailable", code: "download_in_progress" };
      await handleRedownload(signal);
      return { status: "success", uiState: "download_requested", data: { format: "PDF" } };
    },
  });

  // Compute summary metrics for the success screen
  const volumeMetrics = computeFunnelVolumeMetrics(
    wizardState.stages,
    wizardState.projectionInputs,
    wizardState.trafficChannels,
    wizardState.graph,
  );
  const totalVisitors = volumeMetrics.visitors;
  const finalVolume = volumeMetrics.customers;
  const targetProjection = scenarioProjection(
    wizardState.stages,
    wizardState.projectionInputs,
    1.0,
    wizardState.trafficChannels,
    wizardState.graph,
  );
  const horizonRevenue = targetProjection.points.at(-1)?.revenue ?? 0;

  const templateLabelKey = `templates.${successState.templateId}.label` as keyof FunnelPlannerContent;
  const templateLabel = (content[templateLabelKey] as string) ?? successState.templateId;
  const plannerDirection = directionForFunnelLocale(locale);
  const plannerTextAlign = textAlignForFunnelLocale(locale);

  return (
    <div
      dir={plannerDirection}
      data-funnel-planner-shell="success"
      data-funnel-planner-direction={plannerDirection}
      data-analytics-component-family="funnel_planner"
      data-analytics-component-id="funnel-planner.success"
      data-analytics-experience-id="xp.funnel-planner.success.control"
      data-analytics-variant-key={successState.templateId}
      className="funnel-planner-shell funnel-planner-shell--success mx-auto max-w-3xl px-4 py-10"
      style={{
        direction: plannerDirection,
        textAlign: plannerTextAlign,
        "--funnel-planner-text-align": plannerTextAlign,
        "--funnel-planner-direction": plannerDirection,
      } as CSSProperties & Record<"--funnel-planner-text-align" | "--funnel-planner-direction", string>}
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M4 11l5 5 9-9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold">{content.successTitle}</h1>
        <p className="mt-2 text-muted-foreground">{content.successSubtitle}</p>
      </div>

      {/* Funnel flow card */}
      <div className="mb-6 rounded-xl border bg-muted/30 p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            {funnelText(content, "successTemplateFunnelTitle", "{template} Funnel", { template: templateLabel })}
          </h2>
          <button
            type="button"
            onClick={onEditFunnel}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground border hover:border-primary/40 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M8.5 1.5l2 2-6 6H2.5v-2l6-6z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {funnelText(content, "successEditFunnelButton", "Edit funnel")}
          </button>
        </div>

        <FunnelFlowDiagram stages={wizardState.stages} volumes={volumeMetrics.stageVolumes} content={content} locale={locale} />

        {/* Key metrics row */}
        <div className="mt-4 border-t pt-3 grid grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-xs text-muted-foreground">{funnelText(content, "successMonthlyVisitors", "Monthly visitors")}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums">{formatFunnelNumber(totalVisitors, locale)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">{funnelText(content, "successFinalStage", "Final-stage / mo")}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums">{formatFunnelNumber(finalVolume, locale)}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground">{funnelText(content, "successEstimatedYearOneRevenue", "Estimated monthly revenue at month {month}", { month: wizardState.projectionInputs.projectionMonths })}</p>
            <p className="mt-0.5 text-base font-bold tabular-nums">
              {horizonRevenue > 0
                ? formatFunnelCurrency(horizonRevenue, locale, wizardState.regionalSettings)
                : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <a
          href={bookingCta.href}
          className="rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          data-analytics-component-family="cta"
          data-analytics-component-id="funnel-planner.success.cta.book-call"
          data-analytics-cta-id="cta.funnel-planner.success.book-call"
          data-analytics-experience-id="xp.funnel-planner.success.control"
          data-analytics-variant-key={successState.templateId}
        >
          {content.bookCallButton}
        </a>
        <button
          type="button"
          onClick={() => void handleRedownload()}
          disabled={redownloading}
          aria-disabled={redownloading}
          className="rounded-md border px-6 py-3 text-sm font-semibold hover:bg-muted disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          data-analytics-component-family="cta"
          data-analytics-component-id="funnel-planner.success.cta.download-again"
          data-analytics-cta-id="cta.funnel-planner.success.download-again"
          data-analytics-experience-id="xp.funnel-planner.success.control"
          data-analytics-variant-key={successState.templateId}
        >
          {redownloading ? content.downloadingButton : content.downloadAgainButton}
        </button>
      </div>

      {relatedMagnets.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-semibold">{content.relatedResourcesTitle}</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {relatedMagnets.map((magnet: { path: string; title: string; description?: string }) => (
              <a
                key={magnet.path}
                href={magnet.path}
                className="rounded-lg border p-4 hover:border-primary/40 transition-colors"
                style={{ textAlign: plannerTextAlign }}
              >
                <p className="font-medium text-sm">{magnet.title}</p>
                {magnet.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{magnet.description}</p>
                )}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
